/**
 * Activity Log Database Operations
 *
 * CRUD operations for tracking all changes and activities.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';

export type ActivityAction =
  // Task actions
  | 'task_created'
  | 'task_updated'
  | 'task_deleted'
  | 'task_moved'
  | 'status_changed'
  | 'assignee_added'
  | 'assignee_removed'
  | 'due_date_changed'
  | 'priority_changed'
  | 'tags_changed'
  | 'comment_added'
  | 'comment_updated'
  | 'comment_deleted'
  | 'attachment_added'
  | 'attachment_removed'
  | 'subtask_added'
  | 'subtask_removed'
  | 'estimate_changed'
  | 'time_logged'
  // Sprint actions
  | 'sprint_created'
  | 'sprint_started'
  | 'sprint_completed'
  | 'sprint_updated'
  | 'task_added_to_sprint'
  | 'task_removed_from_sprint'
  // Epic actions
  | 'epic_created'
  | 'epic_updated'
  | 'epic_completed'
  | 'task_added_to_epic'
  | 'task_removed_from_epic'
  // Relation actions
  | 'relation_created'
  | 'relation_deleted'
  // Space actions
  | 'space_created'
  | 'space_updated'
  | 'space_deleted';

export interface ActivityLogEntry {
  id: string;
  taskId: string | null;
  sprintId: string | null;
  epicId: string | null;
  spaceId: string | null;
  userId: string;
  action: ActivityAction;
  fieldChanged: string | null;
  oldValue: unknown;
  newValue: unknown;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface CreateActivityData {
  taskId?: string;
  sprintId?: string;
  epicId?: string;
  spaceId?: string;
  userId: string;
  action: ActivityAction;
  fieldChanged?: string;
  oldValue?: unknown;
  newValue?: unknown;
  metadata?: Record<string, unknown>;
}

// Supabase row type for activity_log table (snake_case from database)
interface SupabaseActivityLogRow {
  id: string;
  task_id: string | null;
  sprint_id: string | null;
  epic_id: string | null;
  space_id: string | null;
  user_id: string;
  action: ActivityAction;
  field_changed: string | null;
  old_value: unknown;
  new_value: unknown;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

// Convert from Supabase (snake_case) to ActivityLogEntry (camelCase)
function convertActivityFromSupabase(row: SupabaseActivityLogRow): ActivityLogEntry {
  return {
    id: row.id,
    taskId: row.task_id,
    sprintId: row.sprint_id,
    epicId: row.epic_id,
    spaceId: row.space_id,
    userId: row.user_id,
    action: row.action,
    fieldChanged: row.field_changed,
    oldValue: row.old_value,
    newValue: row.new_value,
    metadata: row.metadata,
    createdAt: row.created_at,
  };
}

/**
 * Log an activity
 */
export async function logActivity(data: CreateActivityData): Promise<ActivityLogEntry> {
  const insertData = {
    task_id: data.taskId || null,
    sprint_id: data.sprintId || null,
    epic_id: data.epicId || null,
    space_id: data.spaceId || null,
    user_id: data.userId,
    action: data.action,
    field_changed: data.fieldChanged || null,
    old_value: data.oldValue !== undefined ? data.oldValue : null,
    new_value: data.newValue !== undefined ? data.newValue : null,
    metadata: data.metadata || null,
  };

  const { data: created, error } = await withRetry(() =>
    supabase
      .from('activity_log')
      .insert(insertData)
      .select()
      .single()
  );

  if (error || !created) throw error || new Error('Failed to log activity');
  return convertActivityFromSupabase(created);
}

/**
 * Get activity log for a task
 */
export async function getTaskActivity(
  taskId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<ActivityLogEntry[]> {
  const { data, error } = await withRetry(() => {
    let query = supabase
      .from('activity_log')
      .select('*')
      .eq('task_id', taskId)
      .order('created_at', { ascending: false });

    if (options.limit) {
      query = query.limit(options.limit);
    }

    if (options.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
    }

    return query;
  });

  if (error) throw error;
  return (data || []).map(convertActivityFromSupabase);
}

/**
 * Get activity log for a sprint
 */
export async function getSprintActivity(
  sprintId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<ActivityLogEntry[]> {
  const { data, error } = await withRetry(() => {
    let query = supabase
      .from('activity_log')
      .select('*')
      .eq('sprint_id', sprintId)
      .order('created_at', { ascending: false });

    if (options.limit) {
      query = query.limit(options.limit);
    }

    return query;
  });

  if (error) throw error;
  return (data || []).map(convertActivityFromSupabase);
}

/**
 * Get activity log for an epic
 */
export async function getEpicActivity(
  epicId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<ActivityLogEntry[]> {
  const { data, error } = await withRetry(() => {
    let query = supabase
      .from('activity_log')
      .select('*')
      .eq('epic_id', epicId)
      .order('created_at', { ascending: false });

    if (options.limit) {
      query = query.limit(options.limit);
    }

    return query;
  });

  if (error) throw error;
  return (data || []).map(convertActivityFromSupabase);
}

/**
 * Get activity log for a space
 */
export async function getSpaceActivity(
  spaceId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<ActivityLogEntry[]> {
  const { data, error } = await withRetry(() => {
    let query = supabase
      .from('activity_log')
      .select('*')
      .eq('space_id', spaceId)
      .order('created_at', { ascending: false });

    if (options.limit) {
      query = query.limit(options.limit);
    }

    return query;
  });

  if (error) throw error;
  return (data || []).map(convertActivityFromSupabase);
}

/**
 * Get activity log for a user
 */
export async function getUserActivity(
  userId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<ActivityLogEntry[]> {
  const { data, error } = await withRetry(() => {
    let query = supabase
      .from('activity_log')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (options.limit) {
      query = query.limit(options.limit);
    }

    return query;
  });

  if (error) throw error;
  return (data || []).map(convertActivityFromSupabase);
}

/**
 * Get recent global activity
 */
export async function getRecentActivity(
  options: {
    limit?: number;
    spaceId?: string;
    actions?: ActivityAction[];
    since?: Date;
  } = {}
): Promise<ActivityLogEntry[]> {
  const { data, error } = await withRetry(() => {
    let query = supabase
      .from('activity_log')
      .select('*')
      .order('created_at', { ascending: false });

    if (options.spaceId) {
      query = query.eq('space_id', options.spaceId);
    }

    if (options.actions && options.actions.length > 0) {
      query = query.in('action', options.actions);
    }

    if (options.since) {
      query = query.gte('created_at', options.since.toISOString());
    }

    if (options.limit) {
      query = query.limit(options.limit);
    }

    return query;
  });

  if (error) throw error;
  return (data || []).map(convertActivityFromSupabase);
}

// ============================================================================
// Convenience logging functions
// ============================================================================

/**
 * Log task creation
 */
export async function logTaskCreated(
  taskId: string,
  spaceId: string,
  userId: string,
  taskName: string
): Promise<void> {
  await logActivity({
    taskId,
    spaceId,
    userId,
    action: 'task_created',
    newValue: taskName,
  });
}

/**
 * Log task status change
 */
export async function logStatusChange(
  taskId: string,
  spaceId: string,
  userId: string,
  oldStatus: string,
  newStatus: string
): Promise<void> {
  await logActivity({
    taskId,
    spaceId,
    userId,
    action: 'status_changed',
    fieldChanged: 'status',
    oldValue: oldStatus,
    newValue: newStatus,
  });
}

/**
 * Log assignee added
 */
export async function logAssigneeAdded(
  taskId: string,
  spaceId: string,
  userId: string,
  assigneeId: string
): Promise<void> {
  await logActivity({
    taskId,
    spaceId,
    userId,
    action: 'assignee_added',
    fieldChanged: 'assignees',
    newValue: assigneeId,
  });
}

/**
 * Log assignee removed
 */
export async function logAssigneeRemoved(
  taskId: string,
  spaceId: string,
  userId: string,
  assigneeId: string
): Promise<void> {
  await logActivity({
    taskId,
    spaceId,
    userId,
    action: 'assignee_removed',
    fieldChanged: 'assignees',
    oldValue: assigneeId,
  });
}

/**
 * Log comment added
 */
export async function logCommentAdded(
  taskId: string,
  spaceId: string,
  userId: string,
  commentId: string
): Promise<void> {
  await logActivity({
    taskId,
    spaceId,
    userId,
    action: 'comment_added',
    metadata: { commentId },
  });
}

/**
 * Log due date changed
 */
export async function logDueDateChanged(
  taskId: string,
  spaceId: string,
  userId: string,
  oldDueDate: number | null,
  newDueDate: number | null
): Promise<void> {
  await logActivity({
    taskId,
    spaceId,
    userId,
    action: 'due_date_changed',
    fieldChanged: 'dueDate',
    oldValue: oldDueDate,
    newValue: newDueDate,
  });
}

/**
 * Log priority changed
 */
export async function logPriorityChanged(
  taskId: string,
  spaceId: string,
  userId: string,
  oldPriority: string | null,
  newPriority: string | null
): Promise<void> {
  await logActivity({
    taskId,
    spaceId,
    userId,
    action: 'priority_changed',
    fieldChanged: 'priority',
    oldValue: oldPriority,
    newValue: newPriority,
  });
}

/**
 * Log time logged
 */
export async function logTimeLogged(
  taskId: string,
  spaceId: string,
  userId: string,
  duration: number
): Promise<void> {
  await logActivity({
    taskId,
    spaceId,
    userId,
    action: 'time_logged',
    metadata: { duration },
  });
}

/**
 * Log sprint created
 */
export async function logSprintCreated(
  sprintId: string,
  spaceId: string,
  userId: string,
  sprintName: string
): Promise<void> {
  await logActivity({
    sprintId,
    spaceId,
    userId,
    action: 'sprint_created',
    newValue: sprintName,
  });
}

/**
 * Log sprint started
 */
export async function logSprintStarted(
  sprintId: string,
  spaceId: string,
  userId: string
): Promise<void> {
  await logActivity({
    sprintId,
    spaceId,
    userId,
    action: 'sprint_started',
  });
}

/**
 * Log sprint completed
 */
export async function logSprintCompleted(
  sprintId: string,
  spaceId: string,
  userId: string
): Promise<void> {
  await logActivity({
    sprintId,
    spaceId,
    userId,
    action: 'sprint_completed',
  });
}

/**
 * Log task added to sprint
 */
export async function logTaskAddedToSprint(
  taskId: string,
  sprintId: string,
  spaceId: string,
  userId: string
): Promise<void> {
  await logActivity({
    taskId,
    sprintId,
    spaceId,
    userId,
    action: 'task_added_to_sprint',
  });
}

/**
 * Log task removed from sprint
 */
export async function logTaskRemovedFromSprint(
  taskId: string,
  sprintId: string,
  spaceId: string,
  userId: string
): Promise<void> {
  await logActivity({
    taskId,
    sprintId,
    spaceId,
    userId,
    action: 'task_removed_from_sprint',
  });
}

/**
 * Get human-readable description of an activity
 */
export function getActivityDescription(activity: ActivityLogEntry): string {
  switch (activity.action) {
    case 'task_created':
      return `created task "${activity.newValue}"`;
    case 'task_updated':
      return `updated task`;
    case 'task_deleted':
      return `deleted task`;
    case 'status_changed':
      return `changed status from "${activity.oldValue}" to "${activity.newValue}"`;
    case 'assignee_added':
      return `added ${activity.newValue} as assignee`;
    case 'assignee_removed':
      return `removed ${activity.oldValue} from assignees`;
    case 'due_date_changed':
      return `changed due date`;
    case 'priority_changed':
      return `changed priority from "${activity.oldValue}" to "${activity.newValue}"`;
    case 'comment_added':
      return `added a comment`;
    case 'comment_updated':
      return `updated a comment`;
    case 'comment_deleted':
      return `deleted a comment`;
    case 'time_logged':
      return `logged time`;
    case 'sprint_created':
      return `created sprint "${activity.newValue}"`;
    case 'sprint_started':
      return `started the sprint`;
    case 'sprint_completed':
      return `completed the sprint`;
    case 'task_added_to_sprint':
      return `added task to sprint`;
    case 'task_removed_from_sprint':
      return `removed task from sprint`;
    case 'epic_created':
      return `created epic "${activity.newValue}"`;
    case 'epic_completed':
      return `completed the epic`;
    case 'task_added_to_epic':
      return `added task to epic`;
    case 'task_removed_from_epic':
      return `removed task from epic`;
    default:
      return activity.action.replace(/_/g, ' ');
  }
}
